import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import API_BASE from '../../utils/api.js';
import AddressAutocomplete from '../common/AddressAutocomplete';
import {
  DEFAULT_PERMISSIONS,
  PERMISSION_GROUPS,
  GRANULAR_PERMISSION_KEYS,
} from '../../auth/usePermissions';
import { useAuth } from '../../auth/useAuth';
import useProjects from '../../hooks/useProjects';
import '../../component-styles/EmployeeDetail.css';

export default function EmployeeDetail() {
  const { memberIndex } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const idx = parseInt(memberIndex, 10);
  const userId = localStorage.getItem('userId');
  const apiUrl = API_BASE;

  const [activeTab, setActiveTab] = useState('info');
  const [team, setTeam] = useState(null);
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    role: '',
    date: '',
    cost: '',
    clientRate: '',
  });

  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);

  const { projects } = useProjects({ activeOnly: false, isOpen: true });
  const [projectIds, setProjectIds] = useState([]);

  const loadSettings = useCallback(async () => {
    if (!userId) return;
    const res = await fetch(`${apiUrl}/api/settings/${userId}`);
    const data = await res.json();
    const teamData = data.team || [];
    const seatsData = Array.isArray(data.additionalSeats) ? data.additionalSeats : [];
    setTeam(teamData);
    setSeats(seatsData);

    const m = teamData[idx];
    if (m) {
      let firstName = m.firstName || '';
      let lastName = m.lastName || '';
      if (!firstName && !lastName && m.name) {
        const parts = m.name.trim().split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
      setFormData({
        firstName,
        lastName,
        phone: m.phone ?? '',
        email: m.email ?? '',
        street: m.street ?? '',
        city: m.city ?? '',
        state: m.state ?? '',
        zip: m.zip ?? '',
        role: m.role ?? '',
        date: m.date ?? '',
        cost: m.cost ?? '',
        clientRate: m.clientRate ?? '',
      });

      // Identify whether this team row holds a claimed additional seat.
      // Prefer the stable linkedUserId stamped on the row at claim time;
      // fall back to an email match for rows claimed before that linkage
      // existed.
      const memberEmail = (m.email || '').toLowerCase();
      const matchedSeat =
        (m.linkedUserId &&
          seatsData.find((s) => s.status === 'claimed' && s.userId === m.linkedUserId)) ||
        seatsData.find(
          (s) => s.status === 'claimed' && (s.invitedEmail || '').toLowerCase() === memberEmail
        ) ||
        null;

      setPermissions({
        ...DEFAULT_PERMISSIONS,
        ...(matchedSeat ? matchedSeat.permissions || {} : m.permissions || {}),
      });
      setProjectIds(m.projectIds || []);
    }
    setLoading(false);
  }, [apiUrl, userId, idx]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Determine whether this team member holds a claimed additional seat.
  const currentTeamMember = team && team[idx];
  const memberEmail = (formData.email || '').toLowerCase();
  const seat =
    (currentTeamMember?.linkedUserId &&
      seats.find((s) => s.status === 'claimed' && s.userId === currentTeamMember.linkedUserId)) ||
    seats.find(
      (s) => s.status === 'claimed' && (s.invitedEmail || '').toLowerCase() === memberEmail
    ) ||
    null;
  const isSeatHolder = Boolean(seat);
  const claimedSeats = seats.filter((s) => s.status === 'claimed');

  async function saveTeam(patch) {
    const updated = [...team];
    updated[idx] = { ...team[idx], ...patch };
    await fetch(`${apiUrl}/api/settings/${userId}/team`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team: updated }),
    });
    setTeam(updated);
  }

  async function handleSaveInfo(e) {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');
    try {
      const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ');
      await saveTeam({ ...formData, name: fullName });
      setSaveMessage('Saved.');
    } catch {
      setSaveMessage('Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAccess() {
    if (!isSeatHolder || !auth?.idToken || !seat?.userId) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const res = await fetch(`${apiUrl}/api/auth/additional-user-permissions`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
        body: JSON.stringify({ seatUserId: seat.userId, permissions }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save permissions');
      setSaveMessage('Saved.');
    } catch (err) {
      setSaveMessage(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveProjects() {
    setSaving(true);
    setSaveMessage('');
    try {
      await saveTeam({ projectIds });
      setSaveMessage('Saved.');
    } catch {
      setSaveMessage('Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  function toggleProject(projectId) {
    setProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  }

  function switchTab(tab) {
    setActiveTab(tab);
    setSaveMessage('');
  }

  if (loading) {
    return <div className="employee-detail-loading">Loading...</div>;
  }

  if (!team || isNaN(idx) || !team[idx]) {
    return (
      <div className="employee-detail-not-found">
        <p>Team member not found.</p>
        <button onClick={() => navigate('/settings/team')}>Back to Team</button>
      </div>
    );
  }

  return (
    <div className="employee-detail">
      <button className="employee-back-btn" onClick={() => navigate('/settings/team')}>
        <ChevronLeft size={15} />
        Back to Team
      </button>

      <div className="employee-detail-tabs">
        <button
          className={activeTab === 'info' ? 'active' : ''}
          onClick={() => switchTab('info')}
        >
          Employee Info
        </button>
        <button
          className={activeTab === 'access' ? 'active' : ''}
          onClick={() => switchTab('access')}
        >
          Grant Access
        </button>
        <button
          className={activeTab === 'projects' ? 'active' : ''}
          onClick={() => switchTab('projects')}
        >
          Project Access
        </button>
      </div>
      <hr className="employee-detail-divider" />

      {activeTab === 'info' && (
        <form className="employee-info-form" onSubmit={handleSaveInfo}>
          <div className="employee-info-grid">
            <div className="employee-info-col">
              <div className="employee-field">
                <label>First Name</label>
                <input
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </div>
              <div className="employee-field">
                <label>Last Name</label>
                <input
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
              <div className="employee-field">
                <label>Phone Number</label>
                <input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="employee-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="employee-info-col">
              <div className="employee-field">
                <label>Street</label>
                <AddressAutocomplete
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  onSelect={({ street, city, state, zip }) =>
                    setFormData((prev) => ({ ...prev, street, city, state, zip }))
                  }
                  placeholder="Street Address"
                />
              </div>
              <div className="employee-address-row">
                <div className="employee-field">
                  <label>City</label>
                  <input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="employee-field employee-field--short">
                  <label>State</label>
                  <input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="employee-field employee-field--short">
                  <label>Zip Code</label>
                  <input
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>
              <div className="employee-pair-row">
                <div className="employee-field">
                  <label>Role</label>
                  <input
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  />
                </div>
                <div className="employee-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="employee-pair-row">
                <div className="employee-field">
                  <label>Employee Rate</label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  />
                </div>
                <div className="employee-field">
                  <label>Client Rate</label>
                  <input
                    type="number"
                    value={formData.clientRate}
                    onChange={(e) => setFormData({ ...formData, clientRate: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="employee-form-actions">
            <button
              type="button"
              className="employee-cancel-btn"
              onClick={() => navigate('/settings/team')}
            >
              Cancel
            </button>
            <button type="submit" className="employee-save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Update'}
            </button>
            {saveMessage && <span className="employee-save-msg">{saveMessage}</span>}
          </div>
        </form>
      )}

      {activeTab === 'access' && (
        <div className="employee-access">
          {isSeatHolder ? (
            <>
              <p className="employee-section-desc">
                Choose which features <strong>{seat.invitedEmail}</strong> can access.
              </p>

              {/* All Access / Custom Access toggle */}
              <div className="employee-access-mode">
                <label className="employee-access-mode-option">
                  <input
                    type="checkbox"
                    checked={GRANULAR_PERMISSION_KEYS.every((k) => permissions[k] !== false)}
                    onChange={() => {
                      setPermissions((prev) => {
                        const next = { ...prev };
                        for (const k of GRANULAR_PERMISSION_KEYS) next[k] = true;
                        return next;
                      });
                    }}
                  />
                  <span>All Access</span>
                </label>
                <label className="employee-access-mode-option">
                  <input
                    type="checkbox"
                    checked={!GRANULAR_PERMISSION_KEYS.every((k) => permissions[k] !== false)}
                    onChange={() => {}}
                    readOnly
                  />
                  <span>Custom Access</span>
                </label>
              </div>

              {/* Two-column grouped permission grid */}
              <div className="employee-permissions-columns">
                <div className="employee-permissions-col">
                  {PERMISSION_GROUPS.filter((g) => g.column === 'left').map((group) => (
                    <div key={group.id} className="employee-perm-group">
                      <h4 className="employee-perm-group-title">{group.label}</h4>
                      {group.items.map(({ key, label }) => (
                        <label key={key} className="employee-permission-row">
                          <input
                            type="checkbox"
                            checked={permissions[key] !== false}
                            onChange={(e) =>
                              setPermissions((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="employee-permissions-col">
                  {PERMISSION_GROUPS.filter((g) => g.column === 'right').map((group) => (
                    <div key={group.id} className="employee-perm-group">
                      <h4 className="employee-perm-group-title">{group.label}</h4>
                      {group.items.map(({ key, label }) => (
                        <label key={key} className="employee-permission-row">
                          <input
                            type="checkbox"
                            checked={permissions[key] !== false}
                            onChange={(e) =>
                              setPermissions((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="employee-form-actions">
                <button
                  type="button"
                  className="employee-cancel-btn"
                  onClick={() => navigate('/settings/team')}
                >
                  Cancel
                </button>
                <button
                  className="employee-save-btn"
                  onClick={handleSaveAccess}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Update'}
                </button>
                {saveMessage && <span className="employee-save-msg">{saveMessage}</span>}
              </div>
            </>
          ) : (
            <div className="employee-access-locked">
              <p>
                Access permissions only apply to team members with a linked additional-seat
                account. This team member does not have a linked account.
              </p>
              {claimedSeats.length > 0 && (
                <p className="employee-access-locked-hint">
                  Current linked teammates:{' '}
                  <strong>{claimedSeats.map((s) => s.invitedEmail).join(', ')}</strong>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'projects' && (
        <div className="employee-projects">
          <p className="employee-section-desc">
            Select which projects this team member is assigned to.
          </p>
          <div className="employee-project-list">
            {projects.length === 0 && (
              <p className="employee-no-items">No projects found.</p>
            )}
            {projects.map((p) => (
              <label key={p.id} className="employee-project-row">
                <input
                  type="checkbox"
                  checked={projectIds.includes(p.id)}
                  onChange={() => toggleProject(p.id)}
                />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
          <div className="employee-form-actions">
            <button className="employee-save-btn" onClick={handleSaveProjects} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            {saveMessage && <span className="employee-save-msg">{saveMessage}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
